import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

import useJobs from '../../../hooks/useJobs';
import { isAxiosError } from 'axios';
import Page from '../../../components/Page/Page';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import { GetJobsQuery } from '@hud/portal-dtos/pr-comparisons.dto';
import JobsTable from './JobsTable';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { useQueryParamState } from '../../../hooks/useQueryParamState';

type PRComparisonPageQueryParams = CommonTableQueryParams & {
  onlyShowDegraded: boolean;
};

export default function PRComparisonsPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const [totalPages, setTotalPages] = useState<number>(0);

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<PRComparisonPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'endedAt',
      desc: true,

      onlyShowDegraded: false,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
    orderKeyModifier: key => {
      return key.replace("p90Degradation_","p90Degradation.")
    }
  });

  const handleOnlyShowDegradedChange = useCallback(
    (onlyShowDegraded: boolean) => {
      setPageState({ onlyShowDegraded, page: 1 });
    },
    [],
  );

  const jobsQueryParams = useMemo(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetJobsQuery['orderBy'],
      timeFrame,
      environmentName: environment?.name,
      onlyShowDegraded: pageState.onlyShowDegraded,
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      pageState.onlyShowDegraded,
      timeFrame,
      environment?.name,
    ],
  );
  const [debouncedJobsQueryParams] = useDebouncedValue(
    jobsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const jobsQuery = useJobs(debouncedJobsQueryParams);

  useEffect(() => {
    document.title = 'PR Comparisons • Hud';
  }, []);

  useEffect(() => {
    if (jobsQuery.isSuccess) {
      setTotalPages(
        Math.ceil(jobsQuery.data.countFiltered / DEFAULT_PAGE_SIZE),
      );
    }
  }, [jobsQuery.data, jobsQuery.isSuccess]);

  // Analytics
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('pr_comparisons_viewed', {
      environment: environment?.name,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.name]);

  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('pr_comparisons_searched', {
      search: tableState.search,
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedJobsQueryParams.searchTerms]);

  useEffect(() => {
    if (jobsQuery.isSuccess) {
      analytics.capture('pr_comparisons_paginated', {
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (jobsQuery.isSuccess) {
      analytics.capture('pr_comparisons_sorted', {
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy?.key]);

  useEffect(() => {
    if (jobsQuery.isSuccess) {
      analytics.capture('pr_comparisons_only_show_degraded_toggled', {
        toggle_value: pageState.onlyShowDegraded,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.onlyShowDegraded]);

  if (jobsQuery.isError) {
    console.log(jobsQuery.error);
    if (!isAxiosError(jobsQuery.error)) {
      return (
        <div>
          <div>Something went wrong</div>
        </div>
      );
    }
    const error = jobsQuery.error.response?.data;
    return <div>{error?.message ?? 'Something went wrong'}</div>;
  }

  return (
    <Page
      header="PR Comparisons"
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <JobsTable
        jobs={jobsQuery.data?.jobs ?? []}
        loading={jobsQuery.isLoading}
        total={jobsQuery.data?.total}
        countFiltered={jobsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        onlyShowDegraded={pageState.onlyShowDegraded}
        onOnlyShowDegradedChange={handleOnlyShowDegradedChange}
      />
    </Page>
  );
}
